/**
 * `useResolvedAddressOverlaysBatch` — resolve the overlay bundles for a SET of
 * `(chain, address)` refs in one call, and hand back the shared
 * `resolveOverlay(address, chainId)` seam the transpose adapters expect.
 *
 * This is the "second call" the transaction / graph surfaces make after the tx
 * loads: the header seeds the leading from/to inline, then every other address
 * off the decoded body / interaction graph is enriched here in one batched
 * request (leading-only) rather than N per-address reads. Gated centrally by the
 * per-resource {@link overlayConfig} (`resolvedView`), so the whole surface goes
 * dark from one config switch. REST by default — the batch endpoint has no
 * GraphQL FIELD, and a scene or a table is too many refs to alias — with
 * `followTransport` for a surface small enough to honour Settings › APIs. Never
 * un-widenable: the route takes a tenancy tier of its own, so this opens at the
 * caller's configured default view scope like every other read. It did not, once,
 * and the result was the same address showing different attribution in a table
 * than on its own detail page — with the narrower one reading as an address
 * nobody had attributed.
 */
import * as React from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { useApiClient, useOptionalGraphqlClient, useTransport } from "@authe/ui-core/api";
import type { AddressOverlay } from "@authe/fe-commons/transpose";
import type { AddressScope } from "@authe/fe-commons/graphql";
import { overlayConfig, type AttributionResource } from "@/config";
import { useScopePrefs } from "@/app/scopePrefs";
import {
  type AddressProxyContext,
  proxyContextKey,
  type BatchOverlays,
  type OverlayRef,
  type PreparedBatchEntry,
  EMPTY_BATCH_OVERLAYS,
  boundOverlayRefs,
  indexPreparedEntries,
  lookupAddressRow,
  lookupOverlays,
  prepareBatchEntry,
} from "@authe/fe-commons/overlays";
import {
  type AddressOverlayRefEntry,
  addressOverlayRefQueryKey,
  enqueueAddressOverlayRef,
  overlayDiskAccess,
} from "./addressOverlayRefCache";
import { overlayDiskCache } from "./addressOverlayDiskCache";
import { overlayToAddressDecoration } from "./overlayDecoration";

export interface UseResolvedAddressOverlaysBatchOptions {
  proxyContext?: AddressProxyContext;
  /** Automatic enrichment budget. Unset preserves explicit full-batch callers. */
  maxRefs?: number;
  /** Which resource's config gates this view. Default `"address"`. */
  resource?: AttributionResource;
  /** Extra caller gate on top of the config. Default true. */
  enabled?: boolean;
  /** Resolve stored logo keys → URLs for node icons (pass `catalog.resolveLogoUrl`). */
  resolveLogoUrl?: (key: string) => string | undefined;
  /**
   * Also fetch each address's stored row (`lookupAddressRow`) — its risk, type
   * and primary entity. Costs a read per address, so only where something draws
   * them.
   *
   * Part of the cache key, because the two payloads are not interchangeable: a
   * cache hit from a chips-only fetch would otherwise serve a caller that asked
   * for rows a batch with none, and it would read as "no address is known".
   * Callers sharing a ref set should therefore agree on this, or they get a
   * request each.
   */
  includeRow?: boolean;
  /**
   * Also return each hit's `availableScopes` (the "also defined at …" hints).
   * Part of the cache key for the same reason as `includeRow`: a batch fetched
   * without them has none, and serving it would silently drop the hints.
   */
  withScopes?: boolean;
  /**
   * Read over the transport Settings › APIs names for `address.overlays` rather
   * than always REST.
   *
   * Opt-in, for a surface whose set is small (a transaction's participants): the
   * GraphQL read is one aliased field per ref, so a scene or a table stays on the
   * REST batch — and past the alias limit so does this, whatever the setting. A
   * GraphQL failure falls back to REST. Not in the cache key: both transports
   * answer the same question, and changing the setting invalidates every query.
   */
  followTransport?: boolean;
  /**
   * Resolve at an explicit tier instead of the caller's configured default.
   *
   * For a surface with its own widen control. Everything else should leave this
   * alone and inherit Settings › Default view scope, so the same address reads
   * the same way wherever it appears.
   */
  scope?: AddressScope;
  /**
   * Answers the caller's rows already CARRY — the leading catalog block the REST
   * `GET /addresses` list returns on each row, built by the same resolver this
   * batch calls, at the caller's own (`USER`) view.
   *
   * A ref answered here is not requested again, and the result is these answers
   * plus the resolve of whatever is left (a live watch row, a lane that carries
   * no block). The list used to seed this cache under ONE page's refs instead,
   * so the moment a second page loaded — or a watch row arrived, or a partial
   * search narrowed the rows — the table's key stopped matching and every loaded
   * row went back through `POST /addresses/catalog/resolve`.
   *
   * Honoured only when the effective tier IS `USER` and `withScopes` is off: the
   * blocks are the caller's own view and carry no "also defined at" hints, so
   * serving them for a widened read (or one that asked for the hints) would show
   * a narrower answer as the wider one. Otherwise ignored, and every ref is
   * resolved as before.
   */
  carried?: BatchOverlays;
}

export interface ResolvedAddressOverlaysBatchResult {
  requestedCount: number;
  queriedCount: number;
  limit: number;
  truncated: boolean;
  batch: BatchOverlays;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  /**
   * True when overlays are enabled AND at least one ref was queried — or was
   * answered by the rows the caller `carried`, which is an answer too.
   */
  active: boolean;
  /** The neutral decoration seam the transpose adapters (graph/scene) consume. */
  resolveOverlay: (address: string, chainId?: string | null) => AddressOverlay | undefined;
  refetch: () => void;
}

/**
 * Fold the per-ref answers back into the one {@link BatchOverlays} index every
 * consumer of this hook reads through.
 *
 * Each ref is indexed under the chain it was ASKED on, which is the chain the
 * caller will look it up with — `overlayRefKey` canonicalises both sides, so a
 * node holding `0x38` finds an entry resolved as `eip155:56`. A ref the resolve
 * returned nothing for contributes no entry, exactly as before: the lookup
 * answers `undefined` and the surface draws it unattributed.
 */
/**
 * Each resolved entry's index keys, remembered by entry object.
 *
 * The query results keep their `data` identity while unchanged, so the keys
 * (a chain-registry lookup and an address fold each) are derived once per
 * entry rather than once per fold — see `prepareBatchEntry`. Before this the
 * case page re-folded all ~3,000 entries every time one more address resolved:
 * 32 folds of 330 ms during one load, the single largest cost on the page.
 */
const preparedEntries = new WeakMap<AddressOverlayRefEntry, PreparedBatchEntry | null>();

function indexRefEntries(entries: readonly AddressOverlayRefEntry[]): BatchOverlays {
  const folded: PreparedBatchEntry[] = [];
  for (const entry of entries) {
    if (!entry.overlays) continue;
    let ready = preparedEntries.get(entry);
    if (ready === undefined) {
      ready = prepareBatchEntry({
        address: entry.address,
        asOf: entry.asOf,
        blockNumber: entry.blockNumber,
        chain: entry.chainId,
        askedChain: entry.chainId,
        overlays: entry.overlays,
        row: entry.row,
        saved: entry.row != null,
        unconfirmed: entry.status === "unconfirmed",
      });
      preparedEntries.set(entry, ready);
    }
    if (ready) folded.push(ready);
  }
  return folded.length === 0 ? EMPTY_BATCH_OVERLAYS : indexPreparedEntries(folded);
}

export function useResolvedAddressOverlaysBatch(
  refs: OverlayRef[],
  options: UseResolvedAddressOverlaysBatchOptions = {},
): ResolvedAddressOverlaysBatchResult {
  const {
    resource = "address",
    enabled = true,
    resolveLogoUrl,
    includeRow = false,
    withScopes = false,
    followTransport = false,
  } = options;
  const config = overlayConfig(resource);
  const transport = useTransport("address.overlays");
  const graphqlClient = useOptionalGraphqlClient();

  // No explicit tier → the caller's default (Settings › Default view scope),
  // else the product default. `USER` IS the un-widened read, so it maps back to
  // undefined to keep the request body — and its cache key — identical to the
  // plain default read. Mirrors the single-address hook exactly; the two
  // disagreeing about the same setting is the bug this closes.
  const { resolveDefaultResolveScope } = useScopePrefs();
  const effectiveScope = options.scope ?? resolveDefaultResolveScope(resource);
  const scope: AddressScope | undefined =
    effectiveScope === "USER" ? undefined : effectiveScope;

  // What the rows already answered, at the only tier they answer for.
  const carried = scope === undefined && !withScopes && !options.proxyContext ? options.carried : undefined;
  const pending = React.useMemo(
    () => (carried ? unansweredRefs(refs, carried) : refs),
    [refs, carried],
  );
  const answeredByRows = pending.length < refs.length;

  // Dedupe once; the stable key drives both the query cache and the gate.
  const bounded = React.useMemo(
    () => boundOverlayRefs(pending, options.maxRefs ?? pending.length),
    [pending, options.maxRefs],
  );
  const deduped = bounded.refs;

  const gate = enabled && config.enabled && config.resolvedView;
  const fetching = gate && deduped.length > 0;
  const on = fetching || (gate && answeredByRows);
  const client = useApiClient();
  const queryClient = useQueryClient();

  // ONE query per address, not one per set. A ref already cached and fresh never
  // reaches its `queryFn`, so it is not in the request body either; the ones
  // that do reach it are coalesced into a single
  // `POST /addresses/catalog/resolve` for the tick — including refs a sibling
  // surface queued in the same commit. A set that grows by one address costs one
  // address. See `addressOverlayRefCache`.
  const route = React.useMemo(
    () => ({ transport: followTransport ? transport : ("rest" as const), graphqlClient }),
    [followTransport, transport, graphqlClient],
  );
  //
  // Each query also reaches the on-disk copy (`addressOverlayDiskCache`): every
  // server answer is kept, and a query with nothing in memory yet — a reload, a
  // new tab — is answered from disk when the kept answer can be trusted.
  //
  // The option objects are built once per change of their inputs, not per
  // render: with ~3,000 refs on a case feed, deriving every query key (an
  // address fold and a chain lookup each) on each render of the host was a
  // few hundred milliseconds of work that produced the same keys every time.
  const { proxyContext } = options;
  const queries = React.useMemo(
    () =>
      deduped.map((ref) => {
        const queryKey = addressOverlayRefQueryKey(ref, includeRow, scope, withScopes, proxyContext);
        return {
          queryKey,
          enabled: gate,
          queryFn: () =>
            enqueueAddressOverlayRef(
              ref,
              client,
              route,
              { includeRow, withScopes, scope, proxyContext },
              proxyContext ? undefined : overlayDiskAccess(overlayDiskCache(), queryClient, queryKey),
            ),
        };
      }),
    [deduped, includeRow, scope, withScopes, proxyContext, gate, client, route, queryClient],
  );
  const results = useQueries({ queries });

  // `useQueries` hands back a fresh array every render, so the fold is keyed on
  // what actually changed: which refs have answered, and when each last did.
  const stamp = proxyContextKey(options.proxyContext) + results.map((r) => r.dataUpdatedAt).join(",");
  const fetched = React.useMemo(
    () => indexRefEntries(results.flatMap((r) => (r.data ? [r.data] : []))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stamp],
  );
  const batch = React.useMemo(
    () => (carried && gate && answeredByRows ? withCarried(carried, fetched, includeRow) : fetched),
    [carried, gate, answeredByRows, fetched, includeRow],
  );

  // Conservative on purpose, and unchanged in meaning: a caller that reports a
  // count (`useAttributionCoverage`) must not publish one while anything is
  // still in flight, or "not yet resolved" is drawn as "not attributed" and then
  // jumps. Per-ref caching makes partial answers the normal case, so this is the
  // line that keeps that from becoming visible.
  const isLoading = fetching && results.some((r) => r.isLoading);
  const isFetching = results.some((r) => r.isFetching);
  const error = results.find((r) => r.error)?.error ?? null;

  // `useQueries` returns a fresh array every render, so a `refetch` closed over
  // it would change identity every render — and this hook's result is what a
  // graph surface memoises its whole element mapping on. Held in a ref so the
  // callback is stable and a re-render never re-lays-out a canvas.
  const latest = React.useRef(results);
  latest.current = results;
  const refetchAll = React.useCallback(() => {
    // Each refetch re-enqueues, and the window folds them back into one request.
    for (const result of latest.current) void result.refetch();
  }, []);

  const resolveOverlay = React.useCallback(
    (address: string, chainId?: string | null): AddressOverlay | undefined =>
      overlayToAddressDecoration(lookupOverlays(batch, address, chainId), {
        resolveLogoUrl,
        // Empty unless the caller asked for rows, so a chips-only batch behaves
        // exactly as it did — and a caller that wants a hand-set risk band to
        // reach the node passes `includeRow`.
        row: lookupAddressRow(batch, address, chainId),
      }),
    [batch, resolveLogoUrl],
  );

  return React.useMemo(
    () => ({
      batch,
      requestedCount: bounded.requestedCount,
      queriedCount: on ? bounded.queriedCount : 0,
      limit: bounded.limit,
      truncated: on && bounded.truncated,
      isLoading,
      isFetching,
      error,
      active: on,
      resolveOverlay,
      refetch: refetchAll,
    }),
    [batch, on, resolveOverlay, bounded, isLoading, isFetching, error, refetchAll],
  );
}

/** The refs {@link carried} has no answer for — the only ones worth a request. */
function unansweredRefs(refs: OverlayRef[], carried: BatchOverlays): OverlayRef[] {
  return refs.filter((ref) => !lookupOverlays(carried, ref.address, ref.chainId, ref));
}

/**
 * The carried answers with the resolved rest laid over them.
 *
 * The row maps come along only when the caller asked for rows: a batch fetched
 * without `includeRow` has them empty by contract, and a carried row is no more
 * licensed to appear there than a fetched one.
 */
function withCarried(
  carried: BatchOverlays,
  fetched: BatchOverlays,
  includeRow: boolean,
): BatchOverlays {
  const join = <V,>(base: Map<string, V>, over: Map<string, V>) => new Map([...base, ...over]);
  // A saved mark describes a row, so it travels with one: carried only when the
  // rows are, and dropped wherever the fetch answered the same key — that row
  // won the join, and its verdict is the current one.
  const carriedSaved = includeRow
    ? [...carried.savedKeys].filter((key) => !fetched.rowByKey.has(key))
    : [];
  return {
    byKey: join(carried.byKey, fetched.byKey),
    byAddress: join(carried.byAddress, fetched.byAddress),
    rowByKey: includeRow ? join(carried.rowByKey, fetched.rowByKey) : fetched.rowByKey,
    rowByAddress: includeRow ? join(carried.rowByAddress, fetched.rowByAddress) : fetched.rowByAddress,
    savedKeys: new Set([...carriedSaved, ...fetched.savedKeys]),
    // An unconfirmed mark describes the answer it came with: a carried one only
    // stands where the fetch did not answer the same key.
    unconfirmedKeys: new Set([
      ...[...carried.unconfirmedKeys].filter((key) => !fetched.byKey.has(key)),
      ...fetched.unconfirmedKeys,
    ]),
  };
}
